import { useTranslation } from "react-i18next";
import { isCloseToDueDate, isOverDueDate } from "@/shared/utils";
import type { Note, NoteStatus, NoteTypes } from "@/services/api/entities/note/types";

type CardClassesKeys = NonNullable<NoteStatus> | "incoming" | "outgoing" | "note";

type CardClassesValues =
  | "card-to-do"
  | "card-done"
  | "card-in-progress"
  | "card-note"
  | "card-incoming"
  | "card-outgoing";

const cardClasses: Record<CardClassesKeys, CardClassesValues> = {
  TO_DO: "card-to-do",
  IN_PROGRESS: "card-in-progress",
  DONE: "card-done",
  note: "card-note",
  incoming: "card-incoming",
  outgoing: "card-outgoing",
};

export const getCardClass = ({
  dueDate,
  status,
  noteType,
  direction,
}: {
  dueDate: Date | number | string;
  status: NoteStatus;
  noteType: NoteTypes;
  direction: Note["direction"];
}) => {
  if (noteType !== "DEFAULT_COMMITMENT") {
    return cardClasses.note;
  }
  if (status === "DONE") {
    return cardClasses.DONE;
  }
  if (direction === "INCOMING") {
    return cardClasses.incoming;
  }
  if (typeof dueDate === "string" && isOverDueDate({ dueDate })) {
    return cardClasses.TO_DO;
  }
  if (isCloseToDueDate({ dueDate })) {
    return cardClasses.IN_PROGRESS;
  }
  return cardClasses.outgoing;
};

export const getStatusText = (status: NoteStatus) => {
  const { t } = useTranslation();

  switch (status) {
    case "TO_DO":
      return {
        dueText: t("common.status.toDo"),
        color: "date-to-do",
      };
    case "IN_PROGRESS":
      return {
        dueText: t("common.status.inProgress"),
        color: "date-to-do",
      };
    case "DONE":
      return {
        dueText: t("common.status.done"),
        color: "date-done",
      };
    default:
      return {
        dueText: "",
        color: "",
      };
  }
};

export const getStatusColor = ({
  dueDate,
  status,
}: {
  dueDate: Date | number | string;
  status: NoteStatus;
}) => {
  if (status !== "DONE") {
    if (typeof dueDate === "string" && isOverDueDate({ dueDate })) {
      return "status-text-overdue";
    }
    if (isCloseToDueDate({ dueDate })) {
      return "status-text-close-to-due";
    }
  }
  return "";
};

export const noteDateFormat = (stringDate: string) => {
  const parts = stringDate?.split("-");

  if (parts?.length) {
    return new Date(
      parseInt(parts[2], 10),
      parseInt(parts[1], 10) - 1,
      parseInt(parts[0], 10),
    ).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
};
